import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  grammarEntry,
  grammarIndex,
  grammarSearch,
  type GrammarEntry,
  type GrammarSummary,
} from "@/lib/search";

import { GrammarEntryBody } from "./GrammarEntryBody";
import { posFacets, posHead } from "./grammarPos";
import {
  VOLUMES,
  VOLUME_LABEL,
  countLabel,
  filterEntries,
  firstEntry,
  moveFocus,
  toRows,
  type GrammarRow,
} from "./grammarRows";

/**
 * 文法 — the Dictionary of Japanese Grammar, in the app (日本-519).
 *
 * **One list, two sources.** The table of contents and the search results are the same rows in the
 * same shape: typing searches, clearing returns to the contents. What differs is only where the
 * entries come from — a single cached `/api/grammar/index` for the contents, `/api/grammar/search`
 * for a query, because the key rule lives in `engine/grammar_keys.py` and nothing else may run it.
 *
 * **The contents is the book's own order** and that costs one `ORDER BY`: ordering by page within a
 * volume IS DoJG's alphabetical order by the reading's romanisation. So there is no A–Z index —
 * deriving initials from the kana is wrong exactly where a reader would look, since へ sorts at *e*
 * and は at *wa*.
 *
 * **A row OPENS; it does not become a card.** The expanded entry is full bleed on `--panel` with an
 * accent border on all four sides, and its row line keeps a collapsed row's exact geometry, so the
 * concept does not change size or position — only the ▾/▴ turns over. That is the whole of what
 * makes it read as the row opening rather than as a card dropped into the list.
 */

interface Props {
  query: string;
  volume: string | null;
  partOfSpeech: string | null;
  /** The open entry's DoJG id, or null. In the URL, so Read's 開く can deep-link to one. */
  entryId: string | null;
  onQueryChange: (query: string) => void;
  onVolumeChange: (volume: string | null) => void;
  onPartOfSpeechChange: (partOfSpeech: string | null) => void;
  onEntryChange: (entryId: string | null) => void;
  isPhone?: boolean;
  footer?: React.ReactNode;
}

const CHEVRON_DOWN = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const CHEVRON_UP = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

export function Grammar({
  query,
  volume,
  partOfSpeech,
  entryId,
  onQueryChange,
  onVolumeChange,
  onPartOfSpeechChange,
  onEntryChange,
  isPhone = false,
  footer,
}: Props) {
  /**
   * The whole contents, fetched once.
   *
   * 629 rows is ~80 KB and every filter this surface offers is answerable from it, so a request per
   * keystroke would buy nothing. Held for the life of the mount rather than in a module cache: a
   * rail click unmounts this and the re-fetch is one request against a static dataset.
   */
  const [index, setIndex] = useState<GrammarSummary[] | null>(null);
  const [hits, setHits] = useState<GrammarSummary[] | null>(null);
  const [open, setOpen] = useState<GrammarEntry | null>(null);
  const [focused, setFocused] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let live = true;
    grammarIndex()
      .then((answer) => live && setIndex(answer.entries))
      .catch((err) => live && setError(err instanceof Error ? err.message : "目次を読めませんでした。"));
    return () => {
      live = false;
    };
  }, []);

  // Searching. Debounced on the query rather than run per keystroke, and the generation guard is
  // what stops a slow answer for an old query landing over a fast one for the current text.
  const generation = useRef(0);
  useEffect(() => {
    const text = query.trim();
    if (!text) {
      // **The generation is bumped here too, and that is the whole of the fix.** Returning without
      // it left a request already ON THE WIRE still matching `mine`, so clearing the box between
      // the debounce firing and the response landing put the old query's hits back over the
      // contents — under an empty search field, with no way back but a reload.
      generation.current += 1;
      setHits(null);
      return;
    }
    const mine = ++generation.current;
    const timer = window.setTimeout(() => {
      grammarSearch(text)
        .then((answer) => {
          if (generation.current !== mine) return;
          setHits(
            answer.entries.map((entry) => ({
              id: entry.id,
              concept: entry.concept,
              volume: entry.volume,
              page: entry.page,
              partOfSpeech: entry.partOfSpeech,
              meaning: entry.meaning,
            })),
          );
        })
        .catch(() => generation.current === mine && setHits([]));
    }, 160);
    return () => window.clearTimeout(timer);
  }, [query]);

  const source = hits ?? index ?? [];
  const filtered = useMemo(
    () => filterEntries(source, { volume, partOfSpeech }),
    [source, volume, partOfSpeech],
  );
  const rows = useMemo(() => toRows(filtered), [filtered]);
  const facets = useMemo(() => posFacets(index ?? []), [index]);

  // The focus lands on the OPEN entry when there is one and on the first otherwise, whenever the
  // LIST changes — an index kept across a change of list points at a different entry, which is the
  // bug `ResultList` documents about indexing `results` instead of `rows`. The open entry rather
  // than the top because a deep link from Read's 開く names one, and starting ↓ at row 0 of 629
  // would walk away from the thing the reader came for.
  useEffect(() => {
    const at = entryId
      ? rows.findIndex((row) => row.kind === "entry" && row.entry.id === entryId)
      : -1;
    setFocused(at === -1 ? firstEntry(rows) : at);
  }, [rows, entryId]);

  /**
   * Bring the open entry into view.
   *
   * **Without this, Read's 開く lands on the top of a 629-row contents** with the entry expanded a
   * few thousand pixels below — a screen that looks exactly like the plain table of contents, on
   * the app's own headline path into this surface. Keyed on the id so it fires once per entry
   * rather than on every render of the list, and `block: "nearest"` so an entry already on screen
   * does not jump.
   */
  useEffect(() => {
    if (!entryId) return;
    const row = listRef.current?.querySelector(`[data-grammar-id="${CSS.escape(entryId)}"]`);
    row?.scrollIntoView({ block: "nearest" });
  }, [entryId, rows]);

  // Load whatever the URL says is open. Driven by the id rather than by the click, so a deep link
  // from Read's 開く and a press on a row take the same path.
  useEffect(() => {
    if (!entryId) {
      setOpen(null);
      return;
    }
    let live = true;
    grammarEntry(entryId)
      .then((entry) => live && setOpen(entry))
      .catch(() => live && setOpen(null));
    return () => {
      live = false;
    };
  }, [entryId]);

  const toggle = useCallback(
    (id: string) => onEntryChange(entryId === id ? null : id),
    [entryId, onEntryChange],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey || event.isComposing) return;
      const target = event.target as HTMLElement | null;
      // The search field is a text input, so bare keys belong to it — and so is the 品詞 `<select>`,
      // where ArrowUp/ArrowDown are how a native dropdown is changed. Without `SELECT` in this list
      // the handler `preventDefault`ed those and moved the row focus instead, so the filter was
      // unreachable from the keyboard while the list silently scrolled.
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
        if (event.key !== "Enter") return;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const next = moveFocus(rows, focused, event.key === "ArrowDown" ? 1 : -1);
        setFocused(next);
        // Scrolled HERE rather than in an effect on `focused`, because hovering a row sets the same
        // state — an effect would drag the list out from under the pointer on every mouse-move.
        // Only the keyboard can move the focus off screen, so only the keyboard corrects for it.
        const target = rows[next];
        if (target?.kind === "entry") {
          listRef.current
            ?.querySelector(`[data-grammar-id="${CSS.escape(target.entry.id)}"]`)
            ?.scrollIntoView({ block: "nearest" });
        }
        return;
      }
      if (event.key === "Enter") {
        const row = rows[focused];
        if (row?.kind === "entry") {
          event.preventDefault();
          toggle(row.entry.id);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rows, focused, toggle]);

  const segmented = (
    <div
      data-check="grammar.volumes"
      className="flex items-center gap-[2px] rounded-[var(--r-2)] border border-line bg-raised p-[2px]"
    >
      {[null, ...VOLUMES].map((value) => {
        const on = volume === value;
        return (
          <button
            key={value ?? "all"}
            type="button"
            data-check="grammar.volume"
            data-volume={value ?? "all"}
            data-on={on ? "1" : undefined}
            onClick={() => onVolumeChange(value)}
            className={`rounded-[var(--r-2)] px-[13px] py-[7px] font-jp text-meta font-semibold ${
              on ? "bg-accent text-ink-bright" : "text-ink-3"
            }`}
          >
            {value === null ? "全巻" : VOLUME_LABEL[value]}
          </button>
        );
      })}
    </div>
  );

  const posFilter = (
    <label className="flex h-[42px] items-center gap-2 rounded-[var(--r-2)] border border-line-strong bg-void px-3">
      <span className="font-jp text-meta text-ink-4">品詞</span>
      <select
        data-check="grammar.pos-filter"
        value={partOfSpeech ?? ""}
        onChange={(event) => onPartOfSpeechChange(event.target.value || null)}
        /* **`bg-void`, not `bg-transparent`, and it is about the POPUP rather than the control.**
           `:root { color-scheme: dark }` is already set, but Chrome derives a `<select>`'s native
           dropdown background from the element's OWN computed `background-color` — transparent
           falls back to the light default, so 37 options inheriting `--ink-2` drew as
           near-invisible light-on-white (Alex, 30 Aug 2026). `ModelsPanel.tsx` is the working
           precedent: same control, `bg-void`, never reported. The `<label>` around this already
           carries `bg-void`, so the closed state is unchanged.

           The `option` rules are a guard rather than the fix. That panel needs none, but its list
           is a handful of models where this is 37 heads, and an option list inheriting the
           control's ink is exactly what went wrong. */
        className="bg-void font-jp text-meta font-semibold text-ink-2 outline-none [&>option]:bg-void [&>option]:text-ink"
      >
        <option value="">すべて</option>
        {facets.map((facet) => (
          <option key={facet.source} value={facet.source}>
            {posHead(facet.source)}
          </option>
        ))}
      </select>
    </label>
  );

  const filtering = volume !== null || partOfSpeech !== null;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="flex h-[50px] shrink-0 items-center gap-3 border-b border-line bg-topbar px-[18px]">
        <span className="font-jp text-title font-semibold text-ink">文法</span>
        <span className="font-jp text-meta text-ink-4">日本語文法辞典 · 全3巻</span>
      </div>

      <div
        className={`shrink-0 border-b border-line bg-void px-[18px] py-3 ${isPhone ? "flex flex-col gap-[9px]" : "flex items-center gap-3"}`}
      >
        <div className="flex h-[42px] min-w-0 flex-1 items-center gap-[10px] rounded-[var(--r-2)] border border-accent-line bg-panel px-3">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8e8c86" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" />
            <line x1="16.5" y1="16.5" x2="21" y2="21" />
          </svg>
          <input
            data-check="grammar.search"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="文法を検索"
            className="min-w-0 flex-1 bg-transparent font-jp text-[18px] text-ink outline-none placeholder:text-ink-4"
          />
          {query && (
            <button
              type="button"
              data-check="grammar.clear"
              onClick={() => onQueryChange("")}
              className="text-[18px] leading-none text-ink-4"
              aria-label="検索をクリア"
            >
              ×
            </button>
          )}
        </div>
        {segmented}
        {!isPhone && posFilter}
      </div>

      <div className="flex shrink-0 items-baseline gap-3 border-b border-line px-[18px] py-[9px]">
        <span data-check="grammar.count" className="font-jp text-meta text-ink-3">
          {index === null && hits === null
            ? "読み込み中…"
            : countLabel(query, filtered.length, filtering)}
        </span>
        {!isPhone && (
          <span className="ml-auto font-mono text-meta text-ink-4">↓↑で移動 · ↵で開閉</span>
        )}
      </div>

      <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto">
        {error && <p className="m-0 px-[18px] py-4 font-jp text-meta text-accent-text">{error}</p>}
        {!error && filtered.length === 0 && (index !== null || hits !== null) && (
          <div className="flex flex-col items-center gap-[10px] px-[18px] py-16 text-center">
            <span className="font-jp text-[16px] text-ink-3">
              {query.trim()
                ? `「${query.trim()}」にあたる文法項目はありません。`
                : "この条件にあたる文法項目はありません。"}
            </span>
            <span className="font-jp text-meta text-ink-4">
              辞典は629項。語彙は「検索」で引けます。
            </span>
          </div>
        )}
        {rows.map((row, position) =>
          row.kind === "heading" ? (
            <VolumeHeading key={`h-${row.volume}`} volume={row.volume} count={row.count} />
          ) : (
            <Row
              key={row.entry.id}
              row={row}
              open={entryId === row.entry.id}
              entry={entryId === row.entry.id ? open : null}
              focused={position === focused}
              isPhone={isPhone}
              onFocus={() => setFocused(position)}
              onToggle={() => toggle(row.entry.id)}
            />
          ),
        )}
      </div>
      {footer}
    </div>
  );
}

function VolumeHeading({ volume, count }: { volume: string; count: number }) {
  return (
    <div
      data-check="grammar.heading"
      data-volume={volume}
      className="flex items-baseline gap-[10px] border-b border-line-strong bg-void px-[18px] pb-2 pt-4"
    >
      <span className="font-jp text-title font-semibold tracking-[.1em] text-ink">
        {VOLUME_LABEL[volume] ?? volume}
      </span>
      <span className="font-jp text-meta text-ink-4">{count}項</span>
    </div>
  );
}

/**
 * One row, collapsed or open.
 *
 * **The row LINE is identical in both states** — same 19px concept, same columns, same position.
 * Opening one moves nothing and only the chevron turns over, which is what makes the expansion read
 * as the row opening rather than as a card dropped into the list. The open block is full bleed with
 * an accent border on all four sides (Alex, 30 Aug 2026: not three).
 */
function Row({
  row,
  open,
  entry,
  focused,
  isPhone,
  onFocus,
  onToggle,
}: {
  row: Extract<GrammarRow, { kind: "entry" }>;
  open: boolean;
  entry: GrammarEntry | null;
  focused: boolean;
  isPhone: boolean;
  onFocus: () => void;
  onToggle: () => void;
}) {
  const summary = row.entry;
  const line = isPhone ? (
    <div className="flex min-h-[44px] items-center gap-[10px] px-[14px] py-[11px]">
      <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <div className="flex items-baseline gap-[9px]">
          <span
            data-check="grammar.concept"
            className={`min-w-0 flex-1 font-jp text-[19px]/[1.25] font-semibold ${open ? "text-ink-bright" : "text-ink"}`}
          >
            {summary.concept}
          </span>
          <span className="shrink-0 font-mono text-meta text-ink-4">p.{summary.page}</span>
        </div>
        {summary.meaning && (
          <span className="truncate text-meta text-ink-2">{summary.meaning}</span>
        )}
        <span className="font-jp text-meta text-ink-2">{posHead(summary.partOfSpeech)}</span>
      </div>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center text-ink-4">
        {open ? CHEVRON_UP : CHEVRON_DOWN}
      </span>
    </div>
  ) : (
    /* **The left two columns widen at `2xl`, and only there** (Alex, 30 Aug 2026). The fixed
       widths were set for the 1147 design width and are right there: measured over all 629
       entries, the meaning column gets ~663px at 1147 and 13% of meanings still truncate, so
       taking 100px from it costs real text. Above 1536 it is slack either way — at ~1610 it has
       ~1126px for strings whose median is 300px while 何[(Number)+Counter]も wraps to three lines
       in 178. Stats' own `2xl:` classes are the precedent.

       260 rather than 300: concept overflows 178px on 22 entries (3%), 260px on 2, 300px on none,
       and the last 40px buys those two at every other row's expense. */
    <div className="flex items-center gap-[14px] px-[18px] py-[10px]">
      <span
        data-check="grammar.concept"
        className={`w-[178px] shrink-0 font-jp text-[19px]/[1.25] font-semibold 2xl:w-[260px] ${open ? "text-ink-bright" : "text-ink"}`}
      >
        {summary.concept}
      </span>
      {/* **`--ink-2`, and the size stays at 14px** — Alex asked whether it was at our minimum and
          it is: `text-meta` is the ramp's floor, set 15 Aug 2026 after he called 13px unreadable.
          So the fix is ink rather than size. `--ink-4` is documented as "faint — captions, units,
          secondary metadata", which is not what a primary column of a reference screen is.

          It now shares a size and an ink with the meaning column beside it, which is deliberate:
          the two are told apart by script — Japanese against Latin — and never look alike. */}
      <span className="w-[150px] shrink-0 truncate font-jp text-meta text-ink-2 2xl:w-[190px]">
        {posHead(summary.partOfSpeech)}
      </span>
      <span className="min-w-0 flex-1 truncate text-meta text-ink-2">{summary.meaning ?? "—"}</span>
      <span className="shrink-0 font-mono text-meta text-ink-4">p.{summary.page}</span>
      <span className="flex w-6 shrink-0 items-center justify-center text-ink-4">
        {open ? CHEVRON_UP : CHEVRON_DOWN}
      </span>
    </div>
  );

  if (!open) {
    return (
      <div
        role="button"
        tabIndex={-1}
        data-check="grammar.row"
        data-grammar-id={summary.id}
        onMouseEnter={onFocus}
        onClick={onToggle}
        // **A transparent border on all four sides, not just the bottom one.** The open block
        // carries a 1px accent border all round, so a collapsed row with only `border-b` has a
        // content box 1px narrower and 1px higher — and the concept visibly jumps by that pixel
        // when the row opens. Measured, and it is the whole of the "the row line does not move"
        // property: 62,211 against 63,212.
        className={`cursor-pointer border border-transparent border-b-line-soft ${focused ? "bg-panel" : ""}`}
      >
        {line}
      </div>
    );
  }

  return (
    <div
      data-check="grammar.row"
      data-grammar-id={summary.id}
      data-open="1"
      className="border border-accent-line bg-panel"
    >
      <div role="button" tabIndex={-1} onClick={onToggle} className="cursor-pointer">
        {line}
      </div>
      {entry ? (
        <GrammarEntryBody entry={entry} isPhone={isPhone} />
      ) : (
        <p className="m-0 px-[18px] pb-[18px] font-jp text-meta text-ink-4">読み込み中…</p>
      )}
    </div>
  );
}
